/**
 * AggregateGovernancePage — full-page view for models with many aggregates
 * or high query volume. Complements the sidebar panel which stays as
 * a compact summary. Renders decision timeline, per-aggregate query
 * mix, and top rejection reasons.
 */
import { useEffect, useState, useMemo, Fragment } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layers, TrendingUp, ShieldCheck, ShieldAlert, AlertTriangle, ChevronLeft, PlayCircle, DollarSign, HardDrive, Zap, Sparkles, Building2, BellOff, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { AggregateWizard } from '@/components/semantic-models/AggregateWizard';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const PIE_COLORS = ['#8b5cf6', '#22d3ee', '#f59e0b', '#10b981', '#ec4899', '#f87171', '#a3e635'];

export default function AggregateGovernancePage() {
  const { modelId } = useParams();
  const [summary, setSummary] = useState(null);
  const [routeLog, setRouteLog] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [history, setHistory] = useState([]);
  const [cost, setCost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [presetProposal, setPresetProposal] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [tenantPolicyOpen, setTenantPolicyOpen] = useState(false);
  const [tenantPolicy, setTenantPolicy] = useState({});
  const [digestFreq, setDigestFreq] = useState('hourly');
  const [tenantPolicySaving, setTenantPolicySaving] = useState(false);
  const [reconcileState, setReconcileState] = useState({});   // entity_id -> reconcile result
  const [reconcileBusy, setReconcileBusy] = useState({});

  const runReconcile = async (entityId) => {
    setReconcileBusy(p => ({ ...p, [entityId]: true }));
    try {
      const res = await api.reconcileAggregate(entityId, true);
      setReconcileState(p => ({ ...p, [entityId]: res }));
      if (res.auto_certified) { toast.success('All measures reconcile — auto-certified'); load(); }
      else toast[res.all_match ? 'success' : 'warning'](res.all_match ? 'All measures reconcile' : 'Reconciliation found mismatches');
    } catch (e) { toast.error(e.message || 'Reconcile failed'); }
    finally { setReconcileBusy(p => ({ ...p, [entityId]: false })); }
  };
  const certify = async (entityId, certified) => {
    try {
      await api.certifyAggregate(entityId, certified);
      toast.success(certified ? 'Binding certified' : 'Certification revoked');
      load();
    } catch (e) { toast.error(e.message || 'Certify failed'); }
  };
  const toggleAutoCertify = async (entityId, enabled) => {
    try {
      await api.setAggregateAutoCertify(entityId, enabled);
      toast.success(enabled ? 'Auto-certify on: clean reconciles will certify automatically' : 'Auto-certify off');
      load();
    } catch (e) { toast.error(e.message || 'Failed'); }
  };
  const [expandedPeriods, setExpandedPeriods] = useState({});
  const [reconcileAllBusy, setReconcileAllBusy] = useState(false);
  const [recertifyAllBusy, setRecertifyAllBusy] = useState(false);

  const recertifyAll = async () => {
    setRecertifyAllBusy(true);
    try {
      const res = await api.recertifyAllAggregates(modelId);
      if (res.still_drifting > 0) {
        toast.warning(`Certified ${res.certified} clean — ${res.still_drifting} still drifting, fix the source`);
      } else {
        toast.success(`All ${res.certified} binding${res.certified === 1 ? '' : 's'} reconciled clean & certified`);
      }
      load();
    } catch (e) { toast.error(e.message || 'Recertify all failed'); }
    finally { setRecertifyAllBusy(false); }
  };

  const reconcileAll = async () => {
    setReconcileAllBusy(true);
    try {
      const res = await api.reconcileAllAggregates(modelId);
      if (res.drifted > 0) {
        toast.warning(`Reconciled ${res.reconciled} — ${res.drifted} drifting, ${res.stewards_notified} steward(s) alerted`);
      } else {
        toast.success(`Reconciled ${res.reconciled} — all match${res.auto_certified ? `, ${res.auto_certified} auto-certified` : ''}`);
      }
      load();
    } catch (e) { toast.error(e.message || 'Reconcile all failed'); }
    finally { setReconcileAllBusy(false); }
  };
  const setReconcileCadence = async (entityId, preset) => {
    try {
      await api.updateAggregateReconcileSchedule(entityId, preset);
      toast.success(preset === 'manual' ? 'Auto-reconcile off (manual only)' : `Auto-reconcile set to ${preset}`);
      load();
    } catch (e) { toast.error(e.message || 'Failed'); }
  };
  const snoozeAlerts = async (entityId, hours) => {
    try {
      await api.snoozeAggregateAlerts(entityId, hours);
      toast.success(hours === 0 ? 'Drift alerts unmuted' : `Drift alerts muted for ${hours}h`);
      load();
    } catch (e) { toast.error(e.message || 'Failed'); }
  };
  const recertify = async (entityId) => {
    try {
      const res = await api.recertifyAggregate(entityId);
      if (res.certified) toast.success('Reconciled clean — recertified ✓');
      else toast.warning('Still drifting — fix the source, then recertify');
      if (res.reconcile_state) setReconcileState(p => ({ ...p, [entityId]: res.reconcile_state }));
      load();
    } catch (e) { toast.error(e.message || 'Recertify failed'); }
  };

  const openTenantPolicy = async () => {
    try {
      const res = await api.getAggregateTenantPolicy();
      setTenantPolicy(res.aggregate_policy || {});
      setDigestFreq(res.drift_digest_frequency || 'hourly');
      setTenantPolicyOpen(true);
    } catch (e) { toast.error(e.message || 'Failed to load tenant policy'); }
  };
  const setTenantKey = (key, value) => {
    setTenantPolicy(prev => {
      const next = { ...prev };
      if (value === null || value === undefined) delete next[key];
      else next[key] = value;
      return next;
    });
  };
  const saveTenantPolicy = async () => {
    setTenantPolicySaving(true);
    try {
      await api.updateAggregateTenantPolicy(tenantPolicy);
      await api.updateDriftDigestFrequency(digestFreq);
      toast.success('Tenant defaults saved — models will inherit these');
      setTenantPolicyOpen(false);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setTenantPolicySaving(false); }
  };

  const load = async () => {
    if (!modelId) return;
    setLoading(true);
    try {
      const [gov, log, sug, hist, c] = await Promise.all([
        api.getAggregateGovernance(modelId),
        api.getAggregateRouteLog(modelId, 200),
        api.getAggregateSuggestions(modelId, 7).catch(() => ({ proposals: [] })),
        api.getAggregatePolicyHistory(modelId).catch(() => ({ history: [] })),
        api.getAggregateCost(modelId, 30).catch(() => null),
      ]);
      setSummary(gov);
      setRouteLog(log.entries || []);
      setSuggestions(sug.proposals || []);
      setHistory(hist.history || []);
      setCost(c);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [modelId]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildFromSuggestion = (proposal) => {
    setPresetProposal(proposal);
    setWizardOpen(true);
  };

  // Bucket log entries by day for the decision timeline.
  const timeline = useMemo(() => {
    const map = {};
    for (const e of routeLog) {
      if (!e.occurred_at) continue;
      const day = e.occurred_at.slice(0, 10);
      map[day] = map[day] || { day, canonical: 0, aggregate: 0 };
      if (e.fallback_forced || !e.chosen_entity_id) map[day].canonical++;
      else if (e.chosen_entity_id === e.canonical_entity_id) map[day].canonical++;
      else map[day].aggregate++;
    }
    return Object.values(map).sort((a, b) => a.day.localeCompare(b.day));
  }, [routeLog]);

  // Query mix — how many decisions each aggregate served.
  const queryMix = useMemo(() => {
    const map = {};
    for (const e of routeLog) {
      const label = e.chosen_entity_id || 'canonical';
      map[label] = (map[label] || 0) + 1;
    }
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [routeLog]);

  const aggregates = summary?.aggregates || [];

  return (
    <div className="max-w-7xl mx-auto px-6 py-6 space-y-6" data-testid="aggregate-governance-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to={`/semantic-models?model=${modelId}`} data-testid="gov-back-to-model" className="p-1.5 rounded-lg hover:bg-muted/40 text-muted-foreground hover:text-foreground"><ChevronLeft className="w-4 h-4" /></Link>
          <div>
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-primary/70" />
              <h1 className="text-xl font-heading font-bold">Aggregate Governance</h1>
            </div>
            <p className="text-xs text-muted-foreground">Model: <span className="font-mono">{modelId}</span> · {summary?.total_decisions ?? 0} decisions total</p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={reconcileAll} disabled={reconcileAllBusy || aggregates.length === 0} className="h-8" data-testid="reconcile-all-btn"><ShieldCheck className="w-3 h-3 mr-1" />{reconcileAllBusy ? 'Reconciling…' : 'Reconcile all'}</Button>
        <Button size="sm" variant="outline" onClick={recertifyAll} disabled={recertifyAllBusy || aggregates.length === 0} className="h-8 ml-2" data-testid="recertify-all-btn"><RefreshCw className="w-3 h-3 mr-1" />{recertifyAllBusy ? 'Recertifying…' : 'Recertify all'}</Button>
        <Button size="sm" variant="outline" onClick={async () => {
          try { await api.runAllAggregateDriftChecks(modelId); toast.success('Drift checks complete'); load(); }
          catch (e) { toast.error(e.message); }
        }} className="h-8 ml-2"><PlayCircle className="w-3 h-3 mr-1" />Recheck all</Button>
        <Button size="sm" variant="outline" onClick={openTenantPolicy} className="h-8 ml-2" data-testid="tenant-policy-btn"><Building2 className="w-3 h-3 mr-1" />Tenant defaults</Button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Aggregates" value={aggregates.length} icon={Layers} />
        <StatCard label="Total decisions (7d)" value={routeLog.filter(e => (Date.now() - new Date(e.occurred_at).getTime()) < 7 * 86400000).length} icon={TrendingUp} />
        <StatCard label="Drifted" value={aggregates.filter(a => a.drift_state && !a.drift_state.healthy).length} icon={ShieldAlert} tone={aggregates.some(a => a.drift_state && !a.drift_state.healthy) ? 'danger' : 'ok'} />
        <StatCard label="Zero-hit warnings" value={aggregates.filter(a => a.zero_hit_warning).length} icon={AlertTriangle} tone={aggregates.some(a => a.zero_hit_warning) ? 'warn' : 'ok'} />
      </div>

      {/* Timeline + Query mix */}
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 glass-card rounded-2xl p-4 border border-border/30">
          <p className="text-xs uppercase text-muted-foreground tracking-wider mb-3">Decision Timeline · aggregate vs canonical hits per day</p>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeline}>
                <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', fontSize: 12 }} />
                <Line type="monotone" dataKey="aggregate" stroke="#10b981" strokeWidth={2} dot={false} name="Aggregate hits" />
                <Line type="monotone" dataKey="canonical" stroke="#94a3b8" strokeWidth={2} dot={false} name="Canonical fallbacks" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="glass-card rounded-2xl p-4 border border-border/30">
          <p className="text-xs uppercase text-muted-foreground tracking-wider mb-3">Query Mix</p>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={queryMix} dataKey="value" nameKey="name" outerRadius={70} innerRadius={40} paddingAngle={2}>
                  {queryMix.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Aggregates table */}
      <div className="glass-card rounded-2xl p-4 border border-border/30">
        <p className="text-xs uppercase text-muted-foreground tracking-wider mb-3">Aggregates</p>
        <div className="space-y-2">
          {aggregates.map(a => {
            const rc = reconcileState[a.entity_id] || a.reconcile_state;
            return (
            <div key={a.entity_id} className="p-2 rounded-lg bg-muted/10 border border-border/20 text-xs" data-testid={`gov-row-${a.entity_id}`}>
              <div className="grid grid-cols-6 gap-3 items-center">
              <div className="col-span-2">
                <div className="text-sm font-medium flex items-center gap-1.5">{a.name}
                  {a.certified
                    ? <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[9px]" data-testid={`certified-${a.entity_id}`}><ShieldCheck className="w-2.5 h-2.5 mr-0.5" />certified</Badge>
                    : <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[9px]" data-testid={`unverified-${a.entity_id}`}><AlertTriangle className="w-2.5 h-2.5 mr-0.5" />unverified</Badge>}
                </div>
                <p className="font-mono text-[10px] text-muted-foreground">{a.table_name}</p>
                {a.decomposition_dims?.length > 0 && <p className="text-[9px] text-muted-foreground/70 mt-0.5">baked: <span className="font-mono">{a.decomposition_dims.join(', ')}</span></p>}
              </div>
              <div className="text-muted-foreground text-[10px]">Grain<br /><span className="font-mono text-foreground">{a.grain_columns?.join(' × ')}</span></div>
              <div className="text-muted-foreground text-[10px]">Hits · avg<br /><span className="text-foreground">{a.hit_count} · {a.avg_ms ? `${a.avg_ms}ms` : '—'}</span></div>
              <div className="text-muted-foreground text-[10px]">Rows<br /><span className="text-foreground">{a.row_count_estimate?.toLocaleString() ?? '—'}</span></div>
              <div className="flex items-center gap-1 justify-end flex-wrap">
                {a.drift_state ? (a.drift_state.healthy ? <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[9px]"><ShieldCheck className="w-2.5 h-2.5 mr-0.5" />healthy</Badge> : <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-[9px]"><ShieldAlert className="w-2.5 h-2.5 mr-0.5" />{a.drift_state.max_diff_pct}%</Badge>) : <Badge variant="outline" className="text-[9px]">no probe</Badge>}
                {a.zero_hit_warning && <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[9px]">zero-hit</Badge>}
                {isSnoozed(a) && <Badge className="bg-slate-500/10 text-slate-300 border-slate-500/20 text-[9px]" data-testid={`muted-${a.entity_id}`}><BellOff className="w-2.5 h-2.5 mr-0.5" />muted</Badge>}
                <Button size="sm" variant="outline" className="h-6 text-[9px] px-1.5" onClick={() => runReconcile(a.entity_id)} disabled={reconcileBusy[a.entity_id]} data-testid={`reconcile-${a.entity_id}`}>{reconcileBusy[a.entity_id] ? '…' : 'Reconcile'}</Button>
                <Select value={a.reconcile_schedule?.preset || 'nightly'} onValueChange={(v) => setReconcileCadence(a.entity_id, v)}>
                  <SelectTrigger className="h-6 text-[9px] px-1.5 w-[86px]" title="Auto-reconcile cadence" data-testid={`reconcile-cadence-${a.entity_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly" className="text-[10px]">⟳ hourly</SelectItem>
                    <SelectItem value="nightly" className="text-[10px]">⟳ nightly</SelectItem>
                    <SelectItem value="manual" className="text-[10px]">⟳ manual</SelectItem>
                  </SelectContent>
                </Select>
                {isSnoozed(a)
                  ? <Button size="sm" variant="ghost" className="h-6 text-[9px] px-1.5 text-slate-300" onClick={() => snoozeAlerts(a.entity_id, 0)} title={`Muted until ${new Date(a.alerts_snoozed_until).toLocaleString()}`} data-testid={`unmute-${a.entity_id}`}>unmute</Button>
                  : <Button size="sm" variant="ghost" className="h-6 text-[9px] px-1.5 text-muted-foreground" onClick={() => snoozeAlerts(a.entity_id, 24)} title="Mute drift alerts for 24h while you fix the source" data-testid={`snooze-${a.entity_id}`}><BellOff className="w-2.5 h-2.5 mr-0.5" />snooze</Button>}
                <Button size="sm" variant="ghost" className={`h-6 text-[9px] px-1.5 ${a.auto_certify ? 'text-emerald-400' : 'text-muted-foreground'}`} onClick={() => toggleAutoCertify(a.entity_id, !a.auto_certify)} title="Auto-certify when a clean reconcile passes" data-testid={`auto-certify-${a.entity_id}`}>{a.auto_certify ? '⟳ auto-certify on' : '⟳ auto-certify'}</Button>
                {a.certified
                  ? <Button size="sm" variant="ghost" className="h-6 text-[9px] px-1.5 text-muted-foreground" onClick={() => certify(a.entity_id, false)} data-testid={`revoke-${a.entity_id}`}>Revoke</Button>
                  : <Button size="sm" className="h-6 text-[9px] px-1.5" onClick={() => recertify(a.entity_id)} title="Reconcile and certify in one step" data-testid={`recertify-${a.entity_id}`}><RefreshCw className="w-2.5 h-2.5 mr-0.5" />Recertify</Button>}
              </div>
              </div>
              {rc && rc.measures && (
                <div className="mt-2 pt-2 border-t border-border/20" data-testid={`reconcile-results-${a.entity_id}`}>
                  <p className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1">Reconciliation — summary vs transaction {rc.last_reconcile_at ? `· ${new Date(rc.last_reconcile_at).toLocaleString()}` : ''}</p>
                  <div className="space-y-0.5">
                    {rc.measures.map((m, i) => (
                      <Fragment key={i}>
                      <div className="flex items-center gap-2 text-[10px]" data-testid={`reconcile-measure-${a.entity_id}-${m.measure}`}>
                        <span className="font-mono flex-1 truncate">{m.measure}{m.predicate?.length ? <span className="text-muted-foreground/80"> (WHERE {m.predicate.map(p => `${p.field}${p.operator === 'equals' ? '=' : ' ' + p.operator + ' '}${p.value}`).join(' AND ')})</span> : ''}</span>
                        {m.skipped ? <span className="text-muted-foreground/80">{m.reason}</span> : m.error ? <span className="text-red-400">{m.error}</span> : (<>
                          {m.additivity === 'semi_additive' && <Badge variant="outline" className="text-[8px] border-violet-500/40 text-violet-300" title="Level (semi-additive) — cumulative vs inception-to-date">level</Badge>}
                          <span className="text-muted-foreground">{m.additivity === 'semi_additive' ? 'cum' : 'txn'} <span className="text-foreground font-mono">{m.canonical_total?.toLocaleString()}</span></span>
                          <span className="text-muted-foreground">{m.additivity === 'semi_additive' ? 'level' : 'agg'} <span className="text-foreground font-mono">{m.aggregate_total?.toLocaleString()}</span></span>
                          {m.match ? <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[9px]">match</Badge> : <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-[9px]">{m.diff_pct}% off</Badge>}
                          {m.by_period && (
                            <button className="text-[9px] text-primary/80 hover:text-primary ml-1" onClick={() => setExpandedPeriods(p => ({ ...p, [`${a.entity_id}-${m.measure}`]: !p[`${a.entity_id}-${m.measure}`] }))} data-testid={`by-period-toggle-${a.entity_id}-${m.measure}`}>
                              {m.periods_mismatched > 0 ? `${m.periods_mismatched}/${m.periods_checked} periods off` : `${m.periods_checked} periods ✓`}{expandedPeriods[`${a.entity_id}-${m.measure}`] ? ' ▲' : ' ▼'}
                            </button>
                          )}
                        </>)}
                      </div>
                      {m.by_period && expandedPeriods[`${a.entity_id}-${m.measure}`] && (
                        <div className="ml-3 mb-1 mt-0.5 border-l border-border/20 pl-2 space-y-0.5" data-testid={`by-period-${a.entity_id}-${m.measure}`}>
                          <p className="text-[8px] text-muted-foreground/80 uppercase tracking-wider">
                            by {m.period_column}{m.additivity === 'semi_additive' ? ' · cumulative signed-flows vs inception-to-date level' : ''}
                          </p>
                          {m.by_period.map((p, j) => {
                            const cum = p.canonical_cumulative ?? p.canonical;
                            const lvl = p.aggregate_level ?? p.aggregate;
                            const isLevel = m.additivity === 'semi_additive';
                            return (
                            <div key={j} className="flex items-center gap-2 text-[9px]">
                              <span className="font-mono w-24 truncate">{String(p.period)}</span>
                              <span className="text-muted-foreground">{isLevel ? 'cum' : 'txn'} <span className="text-foreground font-mono">{cum?.toLocaleString()}</span></span>
                              <span className="text-muted-foreground">{isLevel ? 'level' : 'agg'} <span className="text-foreground font-mono">{lvl?.toLocaleString()}</span></span>
                              {p.match ? <span className="text-emerald-400">✓</span> : <span className="text-red-400">{p.diff_pct}%</span>}
                            </div>
                          );})}
                        </div>
                      )}
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );})}
          {aggregates.length === 0 && <p className="text-xs text-muted-foreground/80 py-4 text-center">No aggregates yet — add one from the Semantic Models editor.</p>}
        </div>
      </div>

      {/* Cost dashboard */}
      {cost && cost.aggregates && cost.aggregates.length > 0 && (
        <div className="glass-card rounded-2xl p-4 border border-border/30" data-testid="cost-dashboard">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase text-muted-foreground tracking-wider">Cost & ROI · last {cost.days}d</p>
            <span className="text-[10px] text-muted-foreground/80 font-mono">rate: ${cost.rate_per_ms}/ms</span>
          </div>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <StatCard label="Total storage" value={fmtBytes(cost.totals.storage_bytes)} icon={HardDrive} />
            <StatCard label="Compute spend" value={`$${cost.totals.compute_cost_usd}`} icon={DollarSign} />
            <StatCard label="Time saved (est.)" value={`$${cost.totals.estimated_savings_usd}`} icon={Zap} tone="ok" />
            <StatCard label="Net ROI" value={`$${cost.totals.net_roi_usd}`} icon={DollarSign} tone={cost.totals.net_roi_usd >= 0 ? 'ok' : 'danger'} />
          </div>
          <div className="space-y-1.5">
            {cost.aggregates.map(a => (
              <div key={a.entity_id} className="grid grid-cols-7 gap-3 items-center px-2 py-1.5 rounded-lg bg-muted/10 border border-border/20 text-[10px]" data-testid={`cost-row-${a.entity_id}`}>
                <div className="col-span-2">
                  <p className="text-xs font-medium truncate">{a.name}</p>
                  <p className="font-mono text-[9px] text-muted-foreground truncate">{a.table_name}</p>
                </div>
                <div className="text-muted-foreground">Storage<br /><span className="text-foreground font-mono">{fmtBytes(a.storage_bytes)}</span></div>
                <div className="text-muted-foreground">Hits<br /><span className="text-foreground">{a.hits} · {a.avg_query_ms ?? '—'}ms</span></div>
                <div className="text-muted-foreground">Compute<br /><span className="text-foreground">${a.compute_cost_usd}</span></div>
                <div className="text-muted-foreground">Saved<br /><span className="text-emerald-400">${a.estimated_savings_usd}</span></div>
                <div className="text-muted-foreground">Speedup<br /><span className="text-emerald-400">{a.speedup_x}×</span></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="glass-card rounded-2xl p-4 border border-border/30">
          <p className="text-xs uppercase text-muted-foreground tracking-wider mb-3">Suggested Aggregates <span className="text-[9px] text-muted-foreground/80 normal-case">— derived from the last 7 days of missed queries</span></p>
          <div className="space-y-2">
            {suggestions.map((s, i) => (
              <div key={i} className="p-3 rounded-lg bg-primary/5 border border-primary/20 text-xs" data-testid={`suggestion-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="font-medium">{s.proposed_name}</p>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-primary/10 text-primary border-primary/20 text-[9px]">would&#39;ve caught {s.would_have_caught}</Badge>
                    <Button size="sm" onClick={() => buildFromSuggestion(s)} className="h-6 text-[10px] px-2" data-testid={`build-suggestion-${i}`}><Sparkles className="w-2.5 h-2.5 mr-1" />Build this</Button>
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground">Grain: <span className="font-mono">{s.grain_columns.join(' × ')}</span> · Measures: <span className="font-mono">{Object.entries(s.measure_map).map(([m, spec]) => `${m}:${spec.agg_stored}`).join(', ')}</span></p>
                {s.sample_reasons?.length > 0 && <p className="text-[10px] text-muted-foreground/70 mt-1">Router said: “{s.sample_reasons[0]}”</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Policy change history */}
      {history.length > 0 && (
        <div className="glass-card rounded-2xl p-4 border border-border/30">
          <p className="text-xs uppercase text-muted-foreground tracking-wider mb-3">Policy History</p>
          <div className="space-y-1.5">
            {history.map((h, i) => (
              <div key={i} className="grid grid-cols-4 gap-3 items-start p-2 rounded-lg bg-muted/10 border border-border/20 text-[10px]">
                <div><p className="text-muted-foreground/80">Changed at</p><p className="text-foreground font-mono">{h.changed_at?.slice(0, 19)?.replace('T', ' ')}</p></div>
                <div><p className="text-muted-foreground/80">By</p><p className="text-foreground">{h.changed_by_email || h.changed_by_user_id || '—'}</p></div>
                <div><p className="text-muted-foreground/80">Before</p><pre className="text-[9px] font-mono text-red-300/80 whitespace-pre-wrap">{h.before ? JSON.stringify(h.before, null, 1) : '(none)'}</pre></div>
                <div><p className="text-muted-foreground/80">After</p><pre className="text-[9px] font-mono text-emerald-300/80 whitespace-pre-wrap">{JSON.stringify(h.after, null, 1)}</pre></div>
              </div>
            ))}
          </div>
        </div>
      )}

      <AggregateWizard open={wizardOpen} onOpenChange={(v) => { setWizardOpen(v); if (!v) setPresetProposal(null); }} modelId={modelId} onCreated={() => { load(); setPresetProposal(null); }} presetProposal={presetProposal} />

      <Dialog open={tenantPolicyOpen} onOpenChange={setTenantPolicyOpen}>
        <DialogContent className="max-w-lg" data-testid="tenant-policy-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-sm"><Building2 className="w-4 h-4 text-cyan-400" />Tenant-wide Router Defaults</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-1">
            <p className="text-[10px] text-muted-foreground/70 leading-relaxed">
              Set defaults that apply to every semantic model in your tenant. Individual models can still override any of
              these values. Leaving a field cleared inherits the built-in default.
            </p>
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Hybrid Union</p>
                {tenantPolicy.hybrid_union !== undefined && <button onClick={() => setTenantKey('hybrid_union', null)} className="text-[10px] text-muted-foreground hover:text-destructive" data-testid="tenant-clear-hybrid">clear</button>}
              </div>
              <Select value={tenantPolicy.hybrid_union || ''} onValueChange={(v) => setTenantKey('hybrid_union', v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="tenant-policy-hybrid-union"><SelectValue placeholder="Inherit built-in default" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="always">Always</SelectItem>
                  <SelectItem value="when_stale">When stale</SelectItem>
                  <SelectItem value="when_query_touches_today">When query touches today</SelectItem>
                  <SelectItem value="off">Off</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Sketch Tolerance</p>
                {tenantPolicy.sketch_tolerance !== undefined && <button onClick={() => setTenantKey('sketch_tolerance', null)} className="text-[10px] text-muted-foreground hover:text-destructive" data-testid="tenant-clear-sketch">clear</button>}
              </div>
              <Select value={tenantPolicy.sketch_tolerance || ''} onValueChange={(v) => setTenantKey('sketch_tolerance', v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="tenant-policy-sketch-tolerance"><SelectValue placeholder="Inherit built-in default" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prefer_fast">Prefer fast</SelectItem>
                  <SelectItem value="prefer_exact">Prefer exact</SelectItem>
                  <SelectItem value="per_measure">Per measure</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Drift threshold %</p>
                  {tenantPolicy.drift_threshold_pct !== undefined && <button onClick={() => setTenantKey('drift_threshold_pct', null)} className="text-[10px] text-muted-foreground hover:text-destructive" data-testid="tenant-clear-drift">clear</button>}
                </div>
                <Input type="number" step="0.01" min="0" placeholder="inherit" value={tenantPolicy.drift_threshold_pct ?? ''} onChange={(e) => { const v = e.target.value; setTenantKey('drift_threshold_pct', v === '' ? null : parseFloat(v)); }} className="h-8 text-xs" data-testid="tenant-policy-drift-threshold" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Fallback on drift</p>
                  {tenantPolicy.fallback_on_drift !== undefined && <button onClick={() => setTenantKey('fallback_on_drift', null)} className="text-[10px] text-muted-foreground hover:text-destructive" data-testid="tenant-clear-fallback">clear</button>}
                </div>
                <div className="flex items-center gap-2 h-8">
                  <Checkbox id="tenant-fallback-drift" checked={tenantPolicy.fallback_on_drift === true} onCheckedChange={(v) => setTenantKey('fallback_on_drift', !!v)} data-testid="tenant-policy-fallback-drift" />
                  <label htmlFor="tenant-fallback-drift" className="text-xs text-foreground cursor-pointer">{tenantPolicy.fallback_on_drift === undefined ? '(inherit)' : (tenantPolicy.fallback_on_drift ? 'Enabled' : 'Disabled')}</label>
                </div>
              </div>
            </div>
            <div className="border-t border-border/30 pt-3">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1">Drift digest frequency</p>
              <p className="text-[9px] text-muted-foreground/80 mb-1.5">How often stewards receive the roll-up of drifting aggregates found by the automatic reconcile sweep.</p>
              <Select value={digestFreq} onValueChange={setDigestFreq}>
                <SelectTrigger className="h-8 text-xs" data-testid="digest-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly — as soon as a sweep finds drift</SelectItem>
                  <SelectItem value="daily">Daily — one roll-up per day</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTenantPolicyOpen(false)} className="h-8">Cancel</Button>
            <Button onClick={saveTenantPolicy} disabled={tenantPolicySaving} className="h-8" data-testid="tenant-policy-save">{tenantPolicySaving ? '…' : 'Save Tenant Defaults'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone }) {
  const toneClass = tone === 'danger' ? 'text-red-400' : tone === 'warn' ? 'text-amber-400' : tone === 'ok' ? 'text-emerald-400' : 'text-foreground';
  return (
    <div className="glass-card rounded-2xl p-3 border border-border/30">
      <div className="flex items-center gap-2 text-muted-foreground text-[10px] uppercase tracking-wider mb-1"><Icon className="w-3 h-3" />{label}</div>
      <p className={`text-2xl font-heading font-bold ${toneClass}`}>{value}</p>
    </div>
  );
}

function isSnoozed(a) {
  if (!a || !a.alerts_snoozed_until) return false;
  const t = new Date(a.alerts_snoozed_until).getTime();
  return Number.isFinite(t) && t > Date.now();
}

function fmtBytes(n) {
  if (!n) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let v = Number(n);
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 ? 2 : 1)} ${u[i]}`;
}
